/**
 * Pure auth helper functions extracted from useAuth for testability.
 */

const IMPERSONATION_STORAGE_KEY = "impersonation_session";

/**
 * D2 (GoTrue → Nest cutover): localStorage key holding the Nest-issued refresh
 * token. The Supabase client already persists this token internally, but a
 * near-expiry GoTrue refresh attempt (which can NOT rotate a Nest-minted token)
 * clears the stored Supabase session — so we keep an independent copy to recover
 * the session on reload via POST /auth/refresh.
 *
 * Security: stored in localStorage (not an httpOnly cookie), so it is readable
 * by XSS — but this is PARITY with Supabase/GoTrue, which already persists its
 * own refresh token in localStorage, so it adds no new exposure. The bearer
 * credential (the access token) is short-lived (~60min). A future hardening
 * (post-D4, once the backend owns the whole auth flow) could move refresh tokens
 * to httpOnly cookies.
 */
export const NEST_REFRESH_TOKEN_KEY = "dallali-nest-refresh-token";

/**
 * The PREVIOUS Nest refresh token (the one held before the latest rotation).
 *
 * We keep the last TWO tokens so that if a refresh fails with a 401 — typically
 * a rotation desync where our "current" token is stale (a lost rotation response,
 * or another tab advanced the chain) — we can retry ONCE with the prior token
 * before signing out. Paired with the backend's reuse-overlap window, this turns
 * a benign desync into a silent recovery instead of a forced logout.
 */
export const NEST_PREV_REFRESH_TOKEN_KEY = "dallali-nest-refresh-token-prev";

export function persistNestRefreshToken(
  token: string,
  storage: Pick<Storage, "setItem" | "getItem"> = localStorage,
): void {
  try {
    // Shift the outgoing current token into the "prev" slot (unless unchanged),
    // so a 401 on the new token can retry with the one immediately before it.
    const current = storage.getItem(NEST_REFRESH_TOKEN_KEY);
    if (current && current !== token) {
      storage.setItem(NEST_PREV_REFRESH_TOKEN_KEY, current);
    }
    storage.setItem(NEST_REFRESH_TOKEN_KEY, token);
  } catch {
    // Storage unavailable (private mode / quota) — refresh falls back to the
    // Supabase-held token; non-fatal.
  }
}

export function readNestRefreshToken(
  storage: Pick<Storage, "getItem"> = localStorage,
): string | null {
  try {
    return storage.getItem(NEST_REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** The previous refresh token (one rotation back), or null. */
export function readPrevNestRefreshToken(
  storage: Pick<Storage, "getItem"> = localStorage,
): string | null {
  try {
    return storage.getItem(NEST_PREV_REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearNestRefreshToken(
  storage: Pick<Storage, "removeItem"> = localStorage,
): void {
  try {
    storage.removeItem(NEST_REFRESH_TOKEN_KEY);
    storage.removeItem(NEST_PREV_REFRESH_TOKEN_KEY);
  } catch {
    // Non-fatal.
  }
}

/**
 * D2 (GoTrue → Nest cutover): localStorage key holding the Nest-issued ACCESS
 * token. The Supabase client persists this inside its own session blob, but on a
 * tab-return / reload `getSession()` triggers a GoTrue refresh of the (expired)
 * access token, which CANNOT rotate a Nest-minted token — the failed refresh
 * CLEARS the stored Supabase session, wiping the access token before
 * recoverNestSession() can read it. We keep an independent copy so recovery can
 * still forward it to POST /auth/refresh, which reissues a session for a legacy
 * refresh token that has no nest_sessions row (bounded server-side by
 * REISSUE_ACCESS_GRACE_SECONDS). Without this copy that reissue backstop silently
 * no-ops and the user is force-signed-out (DALLALI-FRONTEND-AQ).
 *
 * Security: same posture as NEST_REFRESH_TOKEN_KEY — localStorage (XSS-readable),
 * but parity with the access token Supabase already stores there, so it adds no
 * new exposure. The access token is the short-lived (~60min) bearer credential.
 */
export const NEST_ACCESS_TOKEN_KEY = "dallali-nest-access-token";

export function persistNestAccessToken(
  token: string,
  storage: Pick<Storage, "setItem"> = localStorage,
): void {
  try {
    storage.setItem(NEST_ACCESS_TOKEN_KEY, token);
  } catch {
    // Storage unavailable (private mode / quota) — recovery falls back to the
    // Supabase-held access token; non-fatal.
  }
}

export function readNestAccessToken(
  storage: Pick<Storage, "getItem"> = localStorage,
): string | null {
  try {
    return storage.getItem(NEST_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearNestAccessToken(
  storage: Pick<Storage, "removeItem"> = localStorage,
): void {
  try {
    storage.removeItem(NEST_ACCESS_TOKEN_KEY);
  } catch {
    // Non-fatal.
  }
}

/**
 * Resolves the effective company ID, checking for an active impersonation session.
 * Returns the impersonation target company ID if present, otherwise the original.
 */
export function getEffectiveCompanyId(
  originalCompanyId: string | null,
  storage: { getItem(key: string): string | null } = localStorage
): string | null {
  try {
    const stored = storage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      const session = JSON.parse(stored) as {
        sessionId: string;
        target: { companyId: string };
      };
      if (session.target?.companyId) {
        return session.target.companyId;
      }
    }
  } catch {
    // If parsing fails, use original
  }
  return originalCompanyId;
}

/**
 * Determines if the user has admin privileges based on their roles.
 */
export function isAdmin(roles: Array<{ role: string }>): boolean {
  return roles.some(r => r.role === "admin" || r.role === "super_admin");
}

/**
 * Resolves the initial active role from session storage or defaults to the first role.
 */
export function resolveActiveRole(
  roles: Array<{ role: string }>,
  storage: { getItem(key: string): string | null } = sessionStorage
): string | null {
  const storedRole = storage.getItem("dallali-active-role");
  if (storedRole && roles.some(r => r.role === storedRole)) {
    return storedRole;
  }
  return roles.length > 0 ? roles[0].role : null;
}

/**
 * Portal-only roles: these roles route users to a non-staff portal (owner
 * self-service, tenant portal, etc.) and do NOT hold staff write permissions
 * such as `units.edit`. When a user ALSO holds a staff role (admin, manager,
 * employee) they must NOT land in a portal-only active-role context while
 * operating the staff dashboard, or RLS will silently block their writes.
 */
const PORTAL_ONLY_ROLES = new Set(["owner", "tenant", "service_provider"]);

/**
 * Staff roles in descending privilege order.
 */
const STAFF_ROLES_ORDERED = [
  "super_admin",
  "admin",
  "manager",
  "employee",
] as const;

/**
 * Reconciles the active role for users operating the STAFF dashboard.
 *
 * Problem: a user who holds both a staff role (admin) and a portal-only role
 * (owner/tenant) may have sessionStorage set to their portal-only role from a
 * prior owner-portal visit. The staff dashboard then sends `x-active-role:
 * owner` on every Supabase call, causing RLS policies that require staff
 * capabilities (e.g. `units.edit`) to silently no-op — the write succeeds with
 * 0 rows updated, yet no error is returned (RLS behaviour).
 *
 * Fix: if the stored role is portal-only but the user ALSO holds a staff role,
 * return the highest-privilege staff role instead. The stored role is left in
 * sessionStorage (deliberate) so a user who MANUALLY switches back to owner
 * portal via the role switcher gets their preference honoured; this function is
 * only called during the auth-load phase on the staff dashboard surface.
 *
 * If the user holds NO staff role (pure owner/tenant), the stored (or default)
 * role is returned unchanged — they should not be on the staff dashboard, and
 * their RLS will correctly block staff-only operations regardless.
 *
 * @param roles         Full list of the user's roles.
 * @param storage       Injected storage (defaults to sessionStorage); pass a
 *                      fake in tests.
 * @returns             The resolved role string, or null if the user has no roles.
 */
export function resolveActiveRoleForStaffSurface(
  roles: Array<{ role: string }>,
  storage: { getItem(key: string): string | null } = sessionStorage,
): string | null {
  if (roles.length === 0) return null;

  const storedRole = storage.getItem("dallali-active-role");

  // If stored role is a staff role (or absent / unknown), fall through to the
  // standard `resolveActiveRole` behaviour — nothing to fix.
  if (!storedRole || !PORTAL_ONLY_ROLES.has(storedRole)) {
    return resolveActiveRole(roles, storage);
  }

  // Stored role is portal-only. Check whether the user also holds a staff role.
  const highestStaffRole = STAFF_ROLES_ORDERED.find(r =>
    roles.some(ur => ur.role === r),
  );

  if (highestStaffRole) {
    // User holds a staff role — use it on the staff surface so RLS writes land
    // under the correct capability. Do NOT persist this to sessionStorage here;
    // useAuth.fetchUserData will persist the resolved role via
    // sessionStorage.setItem(ACTIVE_ROLE_KEY, …) immediately after.
    return highestStaffRole;
  }

  // Pure portal-only user — no staff role; honour stored role as-is.
  return resolveActiveRole(roles, storage);
}

// ── Portal guard helpers (feat/portal-redirect-guard) ─────────────────────

/** Roles that should ONLY access their own portal, never the admin dashboard. */
const PORTAL_ONLY_ROLES_SET = new Set([
  "owner",
  "tenant",
  "employee",
  "service_provider",
]);

/** Roles permitted to access the /dashboard admin surface. */
const DASHBOARD_ROLES = new Set(["super_admin", "admin", "manager"]);

/**
 * Returns true if the given role is portal-only — should NOT see the admin
 * dashboard. Includes employee (whose surface is /portal/employee, not /dashboard).
 */
export function isPortalOnlyRole(role: string | null): boolean {
  return !!role && PORTAL_ONLY_ROLES_SET.has(role);
}

/**
 * Returns true if the given role is allowed on the /dashboard staff surface.
 */
export function isStaffRole(role: string | null): boolean {
  return !!role && DASHBOARD_ROLES.has(role);
}

/** Profile types that may hold a FAL (REGA brokerage) licence — staff + admins. */
const FAL_LICENSE_ROLES = new Set([
  "super_admin",
  "admin",
  "self_admin",
  "manager",
  "employee",
]);

/**
 * Returns true if a user operating as `role` may see and edit the FAL licence.
 * Owners, tenants and service providers never hold one. Mirrors the server
 * rule on PATCH /profiles/me (companies-005).
 */
export function canHoldFalLicense(role: string | null | undefined): boolean {
  return !!role && FAL_LICENSE_ROLES.has(role);
}

/**
 * Map any role string to its canonical portal / dashboard path.
 * Unknown/null roles resolve to /no-access (dead-end, no redirect loop).
 */
export function portalPathForRole(role: string | null): string {
  switch (role) {
    case "owner":
      return "/portal/owner";
    case "tenant":
      return "/portal/tenant";
    case "service_provider":
      return "/portal/provider";
    case "employee":
      return "/portal/employee";
    case "admin":
    case "manager":
      return "/dashboard";
    case "super_admin":
      return "/super-admin";
    default:
      return "/no-access";
  }
}
